import React, { useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Paper,
  Button
} from '@mui/material';
import {
  Lightbulb,
  Timeline,
  EmojiObjects,
  Psychology,
  Engineering,
  Speed,
  CheckCircle,
  Star,
  Groups,
  TrendingUp,
  WorkspacePremium,
  Public,
  Business,
  Handshake,
  Flight,
  Language,
  Email,
  Work
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';

const companyStats = [
  {
    icon: <Speed sx={{ fontSize: 40 }} />,
    value: "2004",
    label: "First to Implement Zero Liquid Discharge",
    color: "#2196F3"
  },
  {
    icon: <Timeline sx={{ fontSize: 40 }} />,
    value: "20+",
    label: "Years of Industry Experience",
    color: "#4CAF50"
  },
  {
    icon: <Public sx={{ fontSize: 40 }} />,
    value: "15+",
    label: "Countries Served",
    color: "#FF9800"
  },
  {
    icon: <Engineering sx={{ fontSize: 40 }} />,
    value: "320M+",
    label: "Liters of Water Treated Daily",
    color: "#009688"
  },
  {
    icon: <Business sx={{ fontSize: 40 }} />,
    value: "82+",
    label: "Projects Completed",
    color: "#E91E63"
  },
  {
    icon: <Groups sx={{ fontSize: 40 }} />,
    value: "200+",
    label: "Satisfied Clients",
    color: "#673AB7"
  }
];

const coreValues = [
  {
    title: "Innovation",
    icon: <Lightbulb />,
    description: "Pioneering advanced water treatment solutions with cutting-edge technology",
    color: "#2196F3"
  },
  {
    title: "Sustainability",
    icon: <EmojiObjects />,
    description: "Committed to environmental stewardship and sustainable practices",
    color: "#4CAF50"
  },
  {
    title: "Excellence",
    icon: <Star />,
    description: "Delivering superior quality and performance in every project",
    color: "#FFC107"
  },
  {
    title: "Integrity",
    icon: <Psychology />,
    description: "Operating with transparency, honesty, and ethical business practices",
    color: "#9C27B0"
  }
];

const expertise = [
  {
    area: "Water Treatment Technology",
    level: 95,
    color: "#2196F3"
  },
  {
    area: "Process Automation",
    level: 90,
    color: "#4CAF50"
  },
  {
    area: "System Integration",
    level: 88,
    color: "#FF5722"
  },
  {
    area: "Quality Management",
    level: 92,
    color: "#9C27B0"
  },
  {
    area: "Environmental Compliance",
    level: 94,
    color: "#FFC107"
  }
];

const milestones = [
  {
    year: "1998",
    title: "Company Founded",
    description: "Established as a specialized water treatment solutions provider"
  },
  {
    year: "2005",
    title: "Global Expansion",
    description: "Expanded operations to international markets"
  },
  {
    year: "2012",
    title: "Innovation Center",
    description: "Launched state-of-the-art R&D facility"
  },
  {
    year: "2018",
    title: "AI Integration",
    description: "Pioneered AI-powered water treatment systems"
  },
  {
    year: "2023",
    title: "Sustainability Award",
    description: "Recognized for environmental leadership"
  }
];

const journeyTimeline = [
  {
    year: "2004",
    title: "Service Provider",
    description: "Started as Service Provider for ZLD Projects",
    icon: <Business sx={{ fontSize: 32, color: '#2196F3' }} />,
    color: '#2196F3'
  },
  {
    year: "2008",
    title: "Solution Provider",
    description: "WTT Technology Services Pvt.Ltd. was registered as a company and upgraded as a complete solution provider",
    icon: <Handshake sx={{ fontSize: 32, color: '#00C853' }} />,
    color: '#00C853'
  },
  {
    year: "2018",
    title: "WTT Italia SRL",
    description: "WTT Italia SRL started in Italy",
    icon: <Flight sx={{ fontSize: 32, color: '#FF4081' }} />,
    color: '#FF4081'
  },
  {
    year: "2022",
    title: "Target Global",
    description: "Re-Branded as WTT International",
    icon: <Language sx={{ fontSize: 32, color: '#7C4DFF' }} />,
    color: '#7C4DFF'
  }
];

const TimelineSection = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Box 
      sx={{ 
        py: { xs: 10, md: 12 },
        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z\' fill=\'%230A4B94\' fill-opacity=\'0.03\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")',
          opacity: 0.5,
          zIndex: 0
        }
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8, position: 'relative', zIndex: 1 }}>
          <Typography 
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            variant="h2" 
            sx={{
              fontWeight: 800,
              color: 'text.primary',
              mb: 2,
              fontSize: { xs: '2.5rem', md: '3rem' },
              background: 'linear-gradient(135deg, #0A4B94 0%, #0277bd 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              position: 'relative',
              display: 'inline-block',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80px',
                height: '4px',
                borderRadius: '2px',
                background: 'linear-gradient(90deg, #0A4B94 0%, #0277bd 100%)'
              }
            }}
          >
            Our Journey
          </Typography>
          <Typography 
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            variant="h6" 
            color="text.secondary"
            sx={{ 
              maxWidth: 600, 
              mx: 'auto',
              mt: 4,
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              lineHeight: 1.6
            }}
          >
            Discover our evolution from a service provider to a global technology leader
          </Typography>
        </Box>

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              position: 'absolute',
              left: { xs: '24px', md: '50%' },
              transform: { xs: 'none', md: 'translateX(-50%)' },
              top: 0,
              bottom: 0,
              width: { xs: '2px', md: '2px' },
              background: 'linear-gradient(180deg, #0A4B94 20%, rgba(10,75,148,0.1) 100%)',
              zIndex: 0
            }}
          />

          <Stack spacing={8}>
            {journeyTimeline.map((milestone, index) => (
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                key={index}
                sx={{
                  position: 'relative',
                  zIndex: 1
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { 
                      xs: 'row',
                      md: index % 2 === 0 ? 'row' : 'row-reverse'
                    },
                    alignItems: 'flex-start',
                    justifyContent: { 
                      xs: 'flex-start',
                      md: 'center'
                    },
                    gap: { xs: 3, md: 6 }
                  }}
                >
                  <Box
                    sx={{
                      position: { xs: 'relative', md: 'relative' },
                      width: { xs: 'auto', md: '50%' },
                      textAlign: { 
                        xs: 'left',
                        md: index % 2 === 0 ? 'right' : 'left'
                      },
                      pr: { 
                        xs: 0,
                        md: index % 2 === 0 ? 6 : 0
                      },
                      pl: { 
                        xs: 0,
                        md: index % 2 === 0 ? 0 : 6
                      }
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 4,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.05)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.08)',
                          borderColor: milestone.color,
                          '& .milestone-year': {
                            color: milestone.color,
                            transform: 'scale(1.05)'
                          },
                          '& .milestone-icon': {
                            background: milestone.color,
                            color: '#fff',
                            transform: 'scale(1.1) rotate(360deg)'
                          }
                        }
                      }}
                    >
                      <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Typography 
                          className="milestone-year"
                          variant="h3" 
                          sx={{ 
                            color: 'text.primary',
                            fontWeight: 800,
                            fontSize: { xs: '2rem', md: '2.5rem' },
                            mb: 2,
                            transition: 'all 0.3s ease'
                          }}
                        >
                          {milestone.year}
                        </Typography>
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            mb: 2,
                            fontWeight: 600,
                            color: 'text.primary',
                            fontSize: { xs: '1.25rem', md: '1.5rem' }
                          }}
                        >
                          {milestone.title}
                        </Typography>
                        <Typography 
                          variant="body1"
                          sx={{ 
                            color: 'text.secondary',
                            lineHeight: 1.7,
                            fontSize: { xs: '1rem', md: '1.1rem' }
                          }}
                        >
                          {milestone.description}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: index % 2 === 0 ? 0 : 'auto',
                          left: index % 2 === 0 ? 'auto' : 0,
                          width: '40%',
                          height: '100%',
                          background: `linear-gradient(${index % 2 === 0 ? '90deg' : '-90deg'}, ${milestone.color}08 0%, transparent 100%)`,
                          opacity: 0.5,
                          borderRadius: 3,
                          transition: 'all 0.3s ease'
                        }}
                      />
                    </Paper>
                  </Box>

                  <Box
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Box
                      className="milestone-icon"
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        bgcolor: 'background.paper',
                        border: '2px solid',
                        borderColor: milestone.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 20px ${milestone.color}40`,
                        position: 'relative',
                        zIndex: 2,
                        transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                        '& svg': {
                          fontSize: 28,
                          transition: 'all 0.3s ease'
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          width: { xs: '24px', md: index % 2 === 0 ? '24px' : '24px' },
                          height: '2px',
                          background: `linear-gradient(${index % 2 === 0 ? '90deg' : '-90deg'}, ${milestone.color} 0%, transparent 100%)`,
                          right: { xs: 'auto', md: index % 2 === 0 ? '-24px' : 'auto' },
                          left: { xs: '-24px', md: index % 2 === 0 ? 'auto' : '-24px' }
                        }
                      }}
                    >
                      {milestone.icon}
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

const StatsSection = () => {
  const [isVisible, setIsVisible] = React.useState(false);
  const statsRef = useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, []);

  const formatValue = (value) => {
    if (typeof value === 'string' && value.includes('+')) {
      const number = parseInt(value);
      return (
        <CountUp
          end={number}
          duration={2.5}
          suffix="+"
          enableScrollSpy
          scrollSpyOnce
        />
      );
    } else if (typeof value === 'string' && value.includes('M+')) {
      const number = parseInt(value);
      return (
        <CountUp
          end={number}
          duration={2.5}
          suffix="M+"
          enableScrollSpy
          scrollSpyOnce
        />
      );
    } else {
      return value;
    }
  };

  return (
    <Box 
      ref={statsRef}
      sx={{ 
        py: { xs: 6, md: 8 },
        background: '#FFFFFF',
        position: 'relative',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            mb: { xs: 4, md: 6 },
            fontWeight: 600,
            color: 'text.primary',
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -12,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80px',
              height: '2px',
              background: '#0288d1',
              borderRadius: '1px'
            }
          }}
        >
          Our Achievements
        </Typography>

        <Grid container spacing={4}>
          {companyStats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%',
                  p: 3,
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .stat-icon': {
                      background: stat.color,
                      color: '#FFFFFF'
                    }
                  }
                }}
              >
                <Box
                  className="stat-icon"
                  sx={{
                    width: 64,
                    height: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '12px',
                    background: `${stat.color}15`,
                    color: stat.color,
                    mb: 3,
                    transition: 'all 0.3s ease'
                  }}
                >
                  {stat.icon}
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    fontWeight: 700,
                    color: 'text.primary',
                    lineHeight: 1.2,
                    mb: 1,
                    minHeight: '3rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {isVisible && formatValue(stat.value)}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    lineHeight: 1.4,
                    maxWidth: '180px'
                  }}
                >
                  {stat.label}
                </Typography>

                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '40px',
                    height: '3px',
                    background: stat.color,
                    opacity: 0.2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      opacity: 1,
                      width: '60px'
                    }
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const About = () => {
  return (
    <Box sx={{ py: 0 }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: '390px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("/images/wtt.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          color: 'white',
          textAlign: 'center',
          mb: 0,
        }}
      >
        <Container 
          maxWidth="lg"
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            pl: { xs: 0, md: 0 }
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ marginLeft: '-1rem' }}
          >
            <Box
              sx={{
                maxWidth: '600px',
                textAlign: 'left',
                position: 'relative',
                p: 4,
                ml: { xs: 2, md: 4 },
                background: 'linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 100%)',
                backdropFilter: 'blur(8px)',
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '4px',
                  height: '100%',
                  background: 'linear-gradient(to bottom, #0288d1, transparent)',
                  borderRadius: '4px 0 0 4px',
                }
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 700,
                  mb: 2,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: '60px',
                    height: '3px',
                    background: 'linear-gradient(90deg, #0288d1, rgba(2,136,209,0.2))',
                    borderRadius: '2px',
                  }
                }}
              >
                About WTT International
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  maxWidth: '500px',
                  opacity: 0.9,
                  textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                  fontSize: { xs: '0.9rem', md: '1.1rem' },
                  lineHeight: 1.6,
                  pl: 0.5
                }}
              >
                Leading the way in water treatment technology with innovation and excellence
              </Typography>
            </Box>
          </motion.div>
        </Container>

        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '40%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.4), transparent)',
            pointerEvents: 'none'
          }}
        />
      </Box>

      {/* Quick Stats Banner */}
      <StatsSection />

      {/* Our Story Section */}
      <Box 
        sx={{ 
          background: 'linear-gradient(to bottom, #f8f9fa, #ffffff)',
          position: 'relative',
          py: { xs: 8, md: 12 },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(10,75,148,0.03) 0%, rgba(255,255,255,0) 50%)',
            zIndex: 0
          }
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={0} alignItems="center">
            {/* Left Column - Image */}
            <Grid item xs={12} md={5}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    mr: { md: 6 },
                    mb: { xs: 6, md: 0 }
                  }}
                >
                  {/* Background Decorative Elements */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -20,
                      left: -20,
                      right: 20,
                      bottom: 20,
                      background: 'linear-gradient(135deg, rgba(10,75,148,0.08) 0%, rgba(2,119,189,0.08) 100%)',
                      borderRadius: '0 60px 0 60px',
                      zIndex: 0
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 20,
                      left: 20,
                      right: -20,
                      bottom: -20,
                      border: '2px solid',
                      borderColor: 'primary.main',
                      borderRadius: '60px 0 60px 0',
                      opacity: 0.1,
                      zIndex: 0
                    }}
                  />

                  {/* Main Image */}
                  <Box
                    component="img"
                    src="/images/venk.jpg"
                    alt="Mr. D. Venkatesh - Founder of WTT International"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '40px 0 40px 0',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      position: 'relative',
                      zIndex: 1,
                      transform: 'scale(1.01)',
                      transition: 'transform 0.6s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  />

                  {/* Founder Name Tag */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -24,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 2,
                      background: '#fff',
                      py: 2,
                      px: 4,
                      borderRadius: '40px',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      border: '1px solid',
                      borderColor: 'grey.100'
                    }}
                  >
                    <Typography 
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        textAlign: 'center',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Mr. D. Venkatesh
                    </Typography>
                    <Typography 
                      variant="subtitle2"
                      sx={{
                        color: 'primary.main',
                        fontSize: '0.875rem',
                        textAlign: 'center',
                        opacity: 0.9
                      }}
                    >
                      Founder
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Grid>

            {/* Right Column - Story */}
            <Grid item xs={12} md={7}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
              >
                <Box sx={{ pl: { md: 6 } }}>
                  <Typography 
                    variant="h2"
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '2rem', md: '2.5rem' },
                      mb: 4,
                      background: 'linear-gradient(135deg, #0A4B94 0%, #0277bd 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '-8px',
                        left: 0,
                        width: '60px',
                        height: '4px',
                        borderRadius: '2px',
                        background: 'linear-gradient(90deg, #0A4B94, transparent)'
                      }
                    }}
                  >
                    Our Story
                  </Typography>

                  <Box 
                    sx={{ 
                      position: 'relative',
                      '&::before': {
                        content: '"""',
                        position: 'absolute',
                        top: '-30px',
                        left: '-40px',
                        fontSize: '8rem',
                        color: 'rgba(10,75,148,0.05)',
                        fontFamily: 'Georgia, serif',
                        lineHeight: 1
                      }
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                        lineHeight: 1.8,
                        color: 'text.primary',
                        mb: 4,
                        position: 'relative',
                        textAlign: 'justify'
                      }}
                    >
                      Founded in 2003 by Mr. D. Venkatesh, WTT International began with a clear mission: to revolutionize industrial water treatment through sustainable innovation. As a pioneer in Zero Liquid Discharge (ZLD) technology, Mr. Venkatesh led WTT to become a trusted partner for over 70 major projects across India and abroad.
                    </Typography>

                    <Box
                      sx={{
                        background: 'linear-gradient(135deg, rgba(10,75,148,0.03) 0%, rgba(255,255,255,0) 100%)',
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.100',
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          bottom: 0,
                          width: 4,
                          background: 'linear-gradient(to bottom, #0A4B94, transparent)',
                          borderRadius: '4px 0 0 4px'
                        }
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { xs: '1.1rem', md: '1.25rem' },
                          lineHeight: 1.8,
                          color: 'text.primary',
                          textAlign: 'justify'
                        }}
                      >
                        From advanced RO systems like our patented "Rewolutte" with 97% water recovery, to tailored effluent solutions for textiles, refineries, and more — WTT is driven by a passion for efficiency, sustainability, and engineering excellence.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Timeline Section */}
      <TimelineSection />

      {/* 3. Vision & Mission Section */}
      <Box sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {/* Mission Section */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: '16px',
                  background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                  }
                }}
              >
                <Typography variant="h4" sx={{ mb: 3, color: '#0288d1', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Engineering sx={{ fontSize: 32, color: '#0288d1' }} />
                  Our Mission
                </Typography>
                <List sx={{ p: 0 }}>
                  <ListItem sx={{ pl: 0, pb: 2, borderLeft: '3px solid', borderColor: '#2196F3', pl: 2, mb: 1.5 }}>
                    <ListItemIcon>
                      <Psychology sx={{ color: '#2196F3', fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Innovation in Water Treatment"
                      secondary="Pioneering advanced technologies and sustainable solutions for industrial water treatment"
                      primaryTypographyProps={{ fontWeight: 600, color: 'text.primary', gutterBottom: true }}
                      secondaryTypographyProps={{ fontSize: '0.95rem', color: 'text.secondary', lineHeight: 1.5 }}
                    />
                  </ListItem>

                  <ListItem sx={{ pl: 0, pb: 2, borderLeft: '3px solid', borderColor: '#4CAF50', pl: 2, mb: 1.5 }}>
                    <ListItemIcon>
                      <Speed sx={{ color: '#4CAF50', fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Technical Excellence"
                      secondary="Delivering high-performance water treatment systems with industry-leading efficiency"
                      primaryTypographyProps={{ fontWeight: 600, color: 'text.primary', gutterBottom: true }}
                      secondaryTypographyProps={{ fontSize: '0.95rem', color: 'text.secondary', lineHeight: 1.5 }}
                    />
                  </ListItem>

                  <ListItem sx={{ pl: 0, pb: 2, borderLeft: '3px solid', borderColor: '#FF9800', pl: 2 }}>
                    <ListItemIcon>
                      <EmojiObjects sx={{ color: '#FF9800', fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Environmental Stewardship"
                      secondary="Implementing eco-friendly solutions that minimize environmental impact"
                      primaryTypographyProps={{ fontWeight: 600, color: 'text.primary', gutterBottom: true }}
                      secondaryTypographyProps={{ fontSize: '0.95rem', color: 'text.secondary', lineHeight: 1.5 }}
                    />
                  </ListItem>
                </List>
              </Paper>
            </motion.div>
          </Grid>

          {/* Vision Section */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: '16px',
                  background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                  }
                }}
              >
                <Typography variant="h4" sx={{ mb: 3, color: '#0288d1', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Public sx={{ fontSize: 32, color: '#0288d1' }} />
                  Our Vision
                </Typography>
                <List sx={{ p: 0 }}>
                  <ListItem sx={{ pl: 0, pb: 2, borderLeft: '3px solid', borderColor: '#9C27B0', pl: 2, mb: 1.5 }}>
                    <ListItemIcon>
                      <Language sx={{ color: '#9C27B0', fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Global Water Solutions"
                      secondary="Expanding our presence worldwide to address global water challenges"
                      primaryTypographyProps={{ fontWeight: 600, color: 'text.primary', gutterBottom: true }}
                      secondaryTypographyProps={{ fontSize: '0.95rem', color: 'text.secondary', lineHeight: 1.5 }}
                    />
                  </ListItem>

                  <ListItem sx={{ pl: 0, pb: 2, borderLeft: '3px solid', borderColor: '#673AB7', pl: 2, mb: 1.5 }}>
                    <ListItemIcon>
                      <WorkspacePremium sx={{ color: '#673AB7', fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Industry Leadership"
                      secondary="Leading the water treatment industry with innovative technological solutions"
                      primaryTypographyProps={{ fontWeight: 600, color: 'text.primary', gutterBottom: true }}
                      secondaryTypographyProps={{ fontSize: '0.95rem', color: 'text.secondary', lineHeight: 1.5 }}
                    />
                  </ListItem>

                  <ListItem sx={{ pl: 0, pb: 2, borderLeft: '3px solid', borderColor: '#00BCD4', pl: 2 }}>
                    <ListItemIcon>
                      <TrendingUp sx={{ color: '#00BCD4', fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Sustainable Growth"
                      secondary="Expanding capabilities while maintaining environmental commitment"
                      primaryTypographyProps={{ fontWeight: 600, color: 'text.primary', gutterBottom: true }}
                      secondaryTypographyProps={{ fontSize: '0.95rem', color: 'text.secondary', lineHeight: 1.5 }}
                    />
                  </ListItem>
                </List>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Box>

      {/* 4. Core Values Section */}
      <Box sx={{ py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Typography variant="h3" sx={{ textAlign: 'center', mb: 3, fontWeight: 700 }}>
            Our Core Values
          </Typography>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 6, color: 'text.secondary', maxWidth: '800px', mx: 'auto' }}>
            The principles that guide our work and define our culture
          </Typography>
          <Grid container spacing={4}>
            {coreValues.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 4,
                    background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease',
                    textAlign: 'center',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 8px 30px ${value.color}20`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      background: `${value.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px',
                      color: value.color,
                    }}
                  >
                    {value.icon}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {value.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {value.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Box>

      {/* 5. Awards & Recognition Section */}
      <Box
        sx={{
          py: { xs: 10, md: 12 },
          background: 'linear-gradient(135deg, #EBF3FA 0%, #F5F9FF 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            background: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-.895-2-2-2-2 .895-2 2 .895 2 2 2zm63 31c1.657 0 3-1.343 3-3s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM34 90c1.657 0 3-1.343 3-3s-.895-2-2-2-2 .895-2 2 .895 2 2 2zm56-76c1.657 0 3-1.343 3-3s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z\' fill=\'%230A4B94\' fill-opacity=\'0.03\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")',
            opacity: 0.5
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '30%',
            background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.8) 100%)'
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Section Title */}
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.25rem', md: '2.75rem' },
                fontWeight: 800,
                background: 'linear-gradient(135deg, #0A4B94 0%, #0277bd 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
                letterSpacing: '-0.02em',
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '80px',
                  height: '4px',
                  borderRadius: '2px',
                  background: 'linear-gradient(90deg, #0A4B94 0%, #0277bd 100%)'
                }
              }}
            >
              Award For Excellence In Water Management
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#555',
                maxWidth: '800px',
                mx: 'auto',
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                lineHeight: 1.6,
                fontWeight: 500,
                mt: 4
              }}
            >
              Recognition for our commitment to sustainable water management and innovative solutions
            </Typography>
          </Box>

          {/* Award Content */}
          <Box sx={{ 
            maxWidth: '1100px', 
            margin: '0 auto',
            display: 'flex',
            gap: { xs: 6, md: 8 },
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            background: '#FFFFFF',
            borderRadius: '24px',
            p: { xs: 3, md: 6 },
            boxShadow: '0 20px 60px rgba(10,75,148,0.1)',
            border: '1px solid rgba(10,75,148,0.05)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '100%',
              background: 'linear-gradient(135deg, rgba(10,75,148,0.03) 0%, rgba(255,255,255,0) 100%)'
            }
          }}>
            {/* Award Image */}
            <Box
              sx={{
                flex: '0 0 45%',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(10,75,148,0.08) 100%)',
                  borderRadius: '20px',
                  zIndex: 1
                }
              }}
            >
              <Box
                component="img"
                src="/images/award.png"
                alt="WTT International receiving Excellence in Water Management Award"
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '20px',
                  boxShadow: '0 15px 45px rgba(10,75,148,0.15)',
                  position: 'relative',
                  zIndex: 0,
                  transform: 'scale(1.02)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.03)',
                    boxShadow: '0 20px 60px rgba(10,75,148,0.2)'
                  }
                }}
              />
            </Box>

            {/* Award Description */}
            <Box sx={{ flex: 1, p: { xs: 2, md: 3 } }}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '1.15rem', md: '1.35rem' },
                  lineHeight: 1.7,
                  color: '#2F2F2F',
                  mb: 4,
                  fontWeight: 500
                }}
              >
                WTT International was honored with the prestigious{' '}
                <Box component="span" sx={{ 
                  fontWeight: 700,
                  color: '#0A4B94',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-2px',
                    left: 0,
                    width: '100%',
                    height: '2px',
                    background: 'linear-gradient(90deg, #0A4B94 0%, #0277bd 100%)',
                    opacity: 0.3
                  }
                }}>
                  "Excellence in Water Management Award"
                </Box>
                {' '}by{' '}
                <Box 
                  component="span" 
                  sx={{ 
                    color: '#0A4B94',
                    fontWeight: 600
                  }}
                >
                  CII (Confederation of Indian Industry)
                </Box>
                , recognizing our advanced water treatment technologies and innovative solutions.
              </Typography>

              {/* Quote Box */}
              <Box
                sx={{
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(10,75,148,0.03) 0%, rgba(255,255,255,0) 100%)',
                  borderRadius: '12px',
                  pl: 4,
                  pr: 5,
                  py: 4,
                  boxShadow: '0 8px 32px rgba(10,75,148,0.05)',
                  border: '1px solid',
                  borderColor: 'rgba(10,75,148,0.1)'
                }}
              >
                {/* Blue Left Border */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    height: '70%',
                    width: '4px',
                    borderRadius: '4px',
                    background: 'linear-gradient(180deg, #0A4B94 0%, #0277bd 100%)'
                  }}
                />
                <Typography
                  sx={{
                    color: '#555',
                    fontSize: { xs: '1.1rem', md: '1.2rem' },
                    lineHeight: 1.7,
                    fontStyle: 'italic',
                    fontWeight: 500,
                    letterSpacing: '0.01em'
                  }}
                >
                  This award was presented as part of the Industrial Water / Waste Management competition 2023, highlighting our commitment to sustainable water management solutions.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* 6. Call to Action Section */}
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Typography variant="h3" sx={{ mb: 3, fontWeight: 700 }}>
            Ready to Work Together?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
            Let's discuss how we can help solve your water treatment challenges
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              color="primary"
              startIcon={<Email />}
              href="/contact"
              sx={{
                borderRadius: '28px',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem'
              }}
            >
              Contact Us
            </Button>
            <Button
              variant="outlined"
              size="large"
              color="primary"
              startIcon={<Work />}
              href="/careers"
              sx={{
                borderRadius: '28px',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem'
              }}
            >
              Join Our Team
            </Button>
          </Stack>
        </motion.div>
      </Box>
    </Box>
  );
};

export default About;
